import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import HeaderCard from '../../components/Header/HeaderCard';
import { apiService } from '../../components/Config/api.service';
import TableList from '../../components/Tools/TableList';
import { RiBuilding2Fill, RiFileList3Fill, RiPriceTag3Fill } from "react-icons/ri";
import {notify} from '../../components/Utils/Notifications';
import CreatableSelect from 'react-select/creatable';
import Drawer from '../../components/Tools/DrawerCustom';
import CategoryManager from './CategoryManager';
import DepartmentManager from './DepartmentManager';
import AssignmentManager from './AssignmentManager';
import { TAG_COLORS } from '../../components/Constantes/Colors';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FaArchive } from "react-icons/fa";
import { aOffreRh, canEditHrData } from '../../components/Auth/permissions';
import AffectationOrganigramme from './AffectationOrganigramme';
import IndicateurOrganigramme from './IndicateurOrganigramme';
import Modal from '../../components/Utils/ModalShow';
import { signalerErreur } from '../../components/Utils/signalerErreur';

function Employees() {
    const { t } = useTranslation();
    const QUICK_FILTER_FIELDS = ['department_id', 'category_ids', 'assignment_id'];

    const [data, setData] = useState([]);
    const [sortBy, setSortBy] = useState({ key: 'name', direction: 'asc' });
    const [pagination, setPagination] = useState({
        offset: 0,
        limit: 25,
        total_pages: 1,
        total_count: 0,
        page: 1,
    });

    const [selectedIds, setSelectedIds] = useState([]);
    // Action groupée de l'organigramme (Yros/Kavero#469, T3), sous l'offre RH.
    const [showOrganigramme, setShowOrganigramme] = useState(false);
    // L'indicateur se relit après une action qui change l'organigramme (T5).
    const [versionIndicateur, setVersionIndicateur] = useState(0);
    const relireIndicateur = () => setVersionIndicateur(v => v + 1);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showCategoryDrawer, setShowCategoryDrawer] = useState(false);
    const [showDepartmentDrawer, setShowDepartmentDrawer] = useState(false);
    const [showAssignmentDrawer, setShowAssignmentDrawer] = useState(false);
    const [showArchived, setShowArchived] = useState(false);
    const [showArchiveConfirmModal, setShowArchiveConfirmModal] = useState(false);
    const [isArchiving, setIsArchiving] = useState(true);
    const [archivePayslipWarning, setArchivePayslipWarning] = useState(null);

    // New states for bulk department/assignment
    const [departments, setDepartments] = useState([]);
    const [departmentOptions, setDepartmentOptions] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [showDeptModal, setShowDeptModal] = useState(false);

    const [assignments, setAssignments] = useState([]);
    const [assignmentOptions, setAssignmentOptions] = useState([]);
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [showAssignmentModal, setShowAssignmentModal] = useState(false);

    const [categoryOptions, setCategoryOptions] = useState([]);
    const handleCreateCategory = async (inputValue) => {
        try {
            const res = await apiService.saveElement('/employee/category', { name: inputValue });
            const newCat = res.data.result;
            const newOption = { value: newCat.id, label: newCat.name };
            setCategories(prev => [...prev, newCat]);
            setSelectedCategory(newOption);
        } catch (e) {
            signalerErreur(e, 'Employees.handleCreateCategory');
            notify(t('employeesPage.labelCreateError'), "", 3);
        }
    };

    // New: creatable handlers for departments & assignments (same pattern as categories)
    const handleCreateDepartment = async (inputValue) => {
        try {
            const res = await apiService.saveElement('/employee/department', { name: inputValue });
            const newDept = res.data.result || res.data; // depending on API shape
            const newOption = { value: newDept.id, label: newDept.name };
            setDepartments(prev => [...prev, newDept]);
            setDepartmentOptions(prev => [...prev, newOption]);
            setSelectedDepartment(newOption);
            notify(t('employeesPage.departmentCreated'), "", 2);
        } catch (e) {
            notify(t('employeesPage.departmentCreateError'), "", 3);
            console.error(e);
        }
    };

    const handleCreateAssignment = async (inputValue) => {
        try {
            const res = await apiService.saveElement('/employee/assignment', { name: inputValue });
            const newAssign = res.data.result || res.data;
            const newOption = { value: newAssign.id, label: newAssign.name };
            setAssignments(prev => [...prev, newAssign]);
            setAssignmentOptions(prev => [...prev, newOption]);
            setSelectedAssignment(newOption);
            notify(t('employeesPage.assignmentCreated'), "", 2);
        } catch (e) {
            notify(t('employeesPage.assignmentCreateError'), "", 3);
            console.error(e);
        }
    };

    const handleAffectCategory = async () => {
        if (!selectedCategory || selectedIds.length === 0) return;
        try {
            await apiService.saveElement('/employees/assign-category', {
                employee_ids: selectedIds,
                category_id: selectedCategory.value,
            });
            notify(t('employeesPage.categoryAssigned'), "", 2);
            fetchEmployees(pagination.page, searchTerm, filterBy);
            setShowModal(false);
            setSelectedCategory('');
        } catch (e) {
            notify(t('employeesPage.categoryAssignError'), "", 3);
            console.error("Erreur lors de l'affectation de la catégorie", e);
        }
    };

    // New: assign department to many employees
    const handleAffectDepartment = async () => {
        if (!selectedDepartment || selectedIds.length === 0) return;
        try {
            await apiService.saveElement('/employees/assign-department', {
                employee_ids: selectedIds,
                department_id: selectedDepartment.value,
            });
            notify(t('employeesPage.departmentAssigned'), "", 2);
            fetchEmployees(pagination.page, searchTerm, filterBy);
            setShowDeptModal(false);
            setSelectedDepartment(null);
            setSelectedIds([]);
        } catch (e) {
            notify(t('employeesPage.departmentAssignError'), "", 3);
            console.error("Erreur lors de l'affectation du département", e);
        }
    };

    // New: assign assignment to many employees
    const handleAffectAssignment = async () => {
        if (!selectedAssignment || selectedIds.length === 0) return;
        try {
            await apiService.saveElement('/employees/assign-assignment', {
                employee_ids: selectedIds,
                assignment_id: selectedAssignment.value,
            });
            notify(t('employeesPage.assignmentAssigned'), "", 2);
            fetchEmployees(pagination.page, searchTerm, filterBy);
            setShowAssignmentModal(false);
            setSelectedAssignment(null);
            setSelectedIds([]);
        } catch (e) {
            notify(t('employeesPage.assignmentAssignError'), "", 3);
            console.error("Erreur lors de l'affectation", e);
        }
    };

    const handleArchive = async (archive = true) => {
        if (selectedIds.length === 0) return;
        setIsArchiving(archive);
        setArchivePayslipWarning(null);
        setShowArchiveConfirmModal(true);
    };

    const confirmArchive = async () => {
        let shouldCloseModal = true;
        try {
            const response = await apiService.saveElement('/employees/archive', {
                employee_ids: selectedIds,
                archive: isArchiving,
                confirm_payslip_cleanup: !!archivePayslipWarning,
            });
            const archiveResponse = response.data?.result || response.data;
            if (archiveResponse?.needs_confirmation) {
                setArchivePayslipWarning(archiveResponse);
                shouldCloseModal = false;
                return;
            }
            notify(isArchiving ? t('employeesPage.archiveSuccess') : t('employeesPage.unarchiveSuccess'), "", 2);
            fetchEmployees(pagination.page, searchTerm, filterBy);
            setSelectedIds([]);
            setArchivePayslipWarning(null);
        } catch (e) {
            notify(isArchiving ? t('employeesPage.archiveError') : t('employeesPage.unarchiveError'), "", 3);
            console.error('Erreur archivage', e);
        } finally {
            if (shouldCloseModal) {
                setShowArchiveConfirmModal(false);
            }
        }
    };

    const [loading, setLoading] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    
    const navigate = useNavigate();

    const pageFromUrl = parseInt(searchParams.get('page') || '1', 10);
    const searchFromUrl = searchParams.get('search') || '';
    const filterFromUrl = searchParams.get('filter_by') ? JSON.parse(searchParams.get('filter_by')) : {};

    const [searchTerm, setSearchTerm] = useState(searchFromUrl);
    const [filterBy, setFilterBy] = useState(filterFromUrl);

    const [showDepartmentColumn, setShowDepartmentColumn] = useState(true);
    const [showAssignmentColumn, setShowAssignmentColumn] = useState(true);

    const columns = [
        { key: 'name', label: t('employeesPage.columnName'), sortable: true, avatar: true},
        { key: 'email', label: t('employeesPage.columnEmail') },
        {
            key: 'poste',
            label: t('employeesPage.columnPosition'),
            render: (value) => (
                <span className="badge bg-[--bg-color]  px-2 py-1 rounded-full">
                    {value.poste || t('employeesPage.noPosition')}
                </span>
            )
        },
        { key: 'manager', label: t('employeesPage.columnManager') },
        // department column controlled by manager
        ...(showDepartmentColumn ? [{
            key: 'department', label: t('employeesPage.columnDepartment'),
            render: (value) => (
                <span className="badge bg-[--bg-color]  px-2 py-1 rounded-full">
                    {value.department ? value.department.name : t('employeesPage.noDepartment')}
                </span>
            )
        }] : []),
        // assignment column controlled by manager
        ...(showAssignmentColumn ? [{
            key: 'assignment', label: t('employeesPage.columnAssignment'),
            render: (value) => (
                <span className="badge bg-[--bg-color]  px-2 py-1 rounded-full">
                    {value.assignment ? value.assignment.name : t('employeesPage.noAssignment')}
                </span>
            )
        }] : []),
        {
            key: 'categories',
            label: t('employeesPage.columnTags'),
            render: (value) => {
                const cats = value.categories || [];
                if (cats.length === 0) {
                    return <span className="text-gray-400 text-sm italic">{t('employeesPage.noTag')}</span>;
                }

                // Organiser les catégories par parent
                const organized = [];
                const parentMap = {};

                cats.forEach(cat => {
                    if (cat.parent) {
                        if (!parentMap[cat.parent.id]) {
                            parentMap[cat.parent.id] = {
                                parent: cat.parent,
                                children: []
                            };
                        }
                        parentMap[cat.parent.id].children.push(cat);
                    } else {
                        organized.push({ category: cat, children: [] });
                    }
                });

                // Ajouter les parents avec enfants
                Object.values(parentMap).forEach(group => {
                    organized.push({
                        category: group.parent,
                        children: group.children
                    });
                });

                const maxDisplay = 2;
                const displayItems = organized.slice(0, maxDisplay);
                const remaining = cats.length - displayItems.reduce((sum, item) => sum + 1 + item.children.length, 0);

                return (
                    <div className="flex flex-wrap gap-1 items-center">
                        {displayItems.map((item, idx) => (
                            <div key={idx} className="flex items-center gap-1">
                                {/* Catégorie principale */}
                                <span 
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border"
                                    style={{
                                        backgroundColor: `${TAG_COLORS[(item.category.color || 1) - 1]}15`,
                                        borderColor: TAG_COLORS[(item.category.color || 1) - 1],
                                        color: TAG_COLORS[(item.category.color || 1) - 1],
                                    }}
                                >
                                    <span 
                                        className="w-2 h-2 rounded-full"
                                        style={{ backgroundColor: TAG_COLORS[(item.category.color || 1) - 1] }}
                                    />
                                    {item.category.name}
                                </span>
                                
                                {/* Sous-catégories */}
                                {item.children.length > 0 && (
                                    <div className="flex items-center gap-1">
                                        <span className="text-gray-400">›</span>
                                        <div className="flex flex-wrap gap-1">
                                            {item.children.slice(0, 2).map(child => (
                                                <span 
                                                    key={child.id}
                                                    className="inline-flex items-center px-2 py-0.5 rounded text-xs"
                                                    style={{
                                                        backgroundColor: `${TAG_COLORS[(child.color || 1) - 1]}10`,
                                                        color: TAG_COLORS[(child.color || 1) - 1],
                                                    }}
                                                >
                                                    {child.name}
                                                </span>
                                            ))}
                                            {item.children.length > 2 && (
                                                <span className="text-xs text-gray-400">
                                                    +{item.children.length - 2}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                        {remaining > 0 && (
                            <span 
                                className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600 font-medium"
                                title={t('employeesPage.otherTags', { count: remaining })}
                            >
                                +{remaining}
                            </span>
                        )}
                    </div>
                );
            }
        },
    ]

    const normalizeFilterBy = (filter = {}) => ({
        ...filter,
        filters: Array.isArray(filter?.filters) ? filter.filters : [],
    });

    const hasActiveFilters = (filter = {}) => {
        const normalized = normalizeFilterBy(filter);
        return normalized.filters.length > 0;
    };

    const findQuickFilter = (field) => {
        const normalized = normalizeFilterBy(filterBy);
        return normalized.filters.find(item => item.field === field);
    };

    const getQuickFilterValue = (field) => {
        const currentFilter = findQuickFilter(field);
        if (!currentFilter) return '';
        if (field === 'category_ids') {
            return Array.isArray(currentFilter.value) ? String(currentFilter.value[0] || '') : String(currentFilter.value || '');
        }
        return String(currentFilter.value || '');
    };

    const handleQuickFilterChange = (field, value) => {
        const normalized = normalizeFilterBy(filterBy);
        const filters = normalized.filters.filter(item => !QUICK_FILTER_FIELDS.includes(item.field) || item.field !== field);

        if (value) {
            filters.push({
                field,
                operator: field === 'category_ids' ? 'in' : '=',
                value: field === 'category_ids' ? [Number(value)] : Number(value),
            });
        }

        const nextFilter = {
            ...normalized,
            filters,
        };

        setFilterBy(nextFilter);
        handlePageChange(1, searchTerm, nextFilter);
    };

    // Un filtre posé par l'indicateur de l'organigramme (T5) remplace celui
    // qu'il aurait posé avant, et ne touche pas aux autres.
    const appliquerFiltreOrganigramme = (filtre) => {
        const normalized = normalizeFilterBy(filterBy);
        const filters = normalized.filters.filter(item => !['parent_id', 'id'].includes(item.field));
        const nextFilter = { ...normalized, filters: [...filters, filtre] };
        setFilterBy(nextFilter);
        handlePageChange(1, searchTerm, nextFilter);
    };

    const clearQuickFilter = (field) => {
        const normalized = normalizeFilterBy(filterBy);
        const filters = normalized.filters.filter(item => item.field !== field);

        if (filters.length === normalized.filters.length) return;

        const nextFilter = {
            ...normalized,
            filters,
        };

        setFilterBy(nextFilter);
        handlePageChange(1, searchTerm, nextFilter);
    };

    const categorySelectOptions = categories.map(cat => ({
        value: String(cat.id),
        label: cat.parent ? `${cat.parent.name} / ${cat.name}` : cat.name,
    }));

    const departmentSelectValue = getQuickFilterValue('department_id');
    const categorySelectValue = getQuickFilterValue('category_ids');
    const assignmentSelectValue = getQuickFilterValue('assignment_id');

    const employeeToolbarFilters = (
        <>
            {showDepartmentColumn && (
                <select
                    className="select select-bordered select-sm min-w-[190px] bg-white"
                    value={departmentSelectValue}
                    onChange={(e) => handleQuickFilterChange('department_id', e.target.value)}
                    aria-label={t('employeesPage.filterByDepartmentAria')}
                >
                    <option value="">{t('employeesPage.allDepartments')}</option>
                    {departmentOptions.map(option => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            )}

            <select
                className="select select-bordered select-sm min-w-[190px] bg-white"
                value={categorySelectValue}
                onChange={(e) => handleQuickFilterChange('category_ids', e.target.value)}
                aria-label={t('employeesPage.filterByTagAria')}
            >
                <option value="">{t('employeesPage.allTags')}</option>
                {categorySelectOptions.map(option => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>

            {showAssignmentColumn && (
                <select
                    className="select select-bordered select-sm min-w-[190px] bg-white"
                    value={assignmentSelectValue}
                    onChange={(e) => handleQuickFilterChange('assignment_id', e.target.value)}
                    aria-label={t('employeesPage.filterByAssignmentAria')}
                >
                    <option value="">{t('employeesPage.allAssignments')}</option>
                    {assignmentOptions.map(option => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            )}
        </>
    );


    const fetchEmployees = (page = 1, search = '', filter = '', sort = sortBy) => {
        setLoading(true);
    
        const offset = (page - 1) * pagination.limit;
    
        const params = new URLSearchParams();
        params.append('offset', offset);
        params.append('limit', pagination.limit);        
        params.append('archived', showArchived );        
        if (search) {
            params.append('search', search);
        }
        if (hasActiveFilters(filter)) {
            params.append('filter_by', JSON.stringify(filter));
        }
        if (sort && sort.key) {
            params.append('sort_by', sort.key);
            params.append('sort_dir', sort.direction);
        }
    
        apiService.getElement(`/employees?${params.toString()}`)
            .then(response => {
                setData(response.data.employees);
                const responseData = response.data;
                if (responseData.pagination) {
                    const currentPage = Math.floor(responseData.pagination.offset / responseData.pagination.limit) + 1;
                    setPagination({
                        ...responseData.pagination,
                        page: currentPage,
                    });
                }
                setLoading(false);
            })
            .catch(error => {
                console.error('There was an error fetching the data!', error);
                setLoading(false);
            });
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortBy.key === key && sortBy.direction === 'asc') {
            direction = 'desc';
        }
        const newSort = { key, direction };
        setSortBy(newSort);
        fetchEmployees(pagination.page, searchTerm, filterBy, newSort);
    };

    useEffect(() => {
        fetchEmployees(pageFromUrl, searchFromUrl, filterFromUrl);
        // eslint-disable-next-line
    }, [searchParams]);

    useEffect(() => {
        // Cet effet se déclenchera chaque fois que showArchived changera.
        handlePageChange(1, searchTerm, filterBy);
        // eslint-disable-next-line
    }, [showArchived]);

    useEffect(() => {
        if (!showDepartmentColumn) {
            clearQuickFilter('department_id');
        }
        // eslint-disable-next-line
    }, [showDepartmentColumn]);

    useEffect(() => {
        if (!showAssignmentColumn) {
            clearQuickFilter('assignment_id');
        }
        // eslint-disable-next-line
    }, [showAssignmentColumn]);

    useEffect(() => {
        apiService.getElement('/employee/categories')
            .then(res => {
                setCategories(res.data.categories || []);
                const options = res.data.categories.map(cat => ({
                    value: cat.id,
                    label: cat.name,
                }));
                setCategoryOptions(options);
                console.log("Categories fetched:", res.data.categories);
            });
        // refresh departments/assignments initial fetch already present below (kept)
        apiService.getElement('/employee/departments')
            .then(res => {
                const depts = res.data.departments || [];
                setDepartments(depts);
                const opts = depts.map(d => ({ value: d.id, label: d.name }));
                setDepartmentOptions(opts);
            })
            .catch(() => {});

        apiService.getElement('/employee/assignments')
            .then(res => {
                const assigns = res.data.assignments || [];
                setAssignments(assigns);
                const opts = assigns.map(a => ({ value: a.id, label: a.name }));
                setAssignmentOptions(opts);
            })
            .catch(() => {});

    }, []);
    
    const handleCloseDepartmentDrawer = () => {
        setShowDepartmentDrawer(false);
        relireIndicateur();
        // refresh departments used by CreatableSelect
        apiService.getElement('/employee/departments')
            .then(res => {
                const depts = res.data.departments || [];
                setDepartments(depts);
                const opts = depts.map(d => ({ value: d.id, label: d.name }));
                setDepartmentOptions(opts);
            })
            .catch(() => {});
    };

    const handleCloseAssignmentDrawer = () => {
        setShowAssignmentDrawer(false);
        apiService.getElement('/employee/assignments')
            .then(res => {
                const assigns = res.data.assignments || [];
                setAssignments(assigns);
                const opts = assigns.map(a => ({ value: a.id, label: a.name }));
                setAssignmentOptions(opts);
            })
            .catch(() => {});
    };

    const handleSelectedRowsChange = (ids) => {
        setSelectedIds(ids);
    };

    const handleCloseCategoryDrawer = () => {
        setShowCategoryDrawer(false);
        fetchEmployees(1, "", "");
    };

    const handlePageChange = (page, search, filter) => {
        const newSearchTerm = search !== undefined ? search : searchTerm;
        const newFilterBy = filter !== undefined ? filter : filterBy;
    
        setSearchTerm(newSearchTerm);
        setFilterBy(newFilterBy);
    
        // Met à jour l’URL
        const params = {};
        if (page > 1) params.page = page;
        if (newSearchTerm) params.search = newSearchTerm;
        if (hasActiveFilters(newFilterBy)) {
            params.filter_by = JSON.stringify(newFilterBy);
        }
        setSearchParams(params);

        fetchEmployees(page, newSearchTerm, newFilterBy);
    };

    const canEdit = canEditHrData();

    const etiquetteButtonProps = {
        label: t('employeesPage.tagButton'),
        className: "btn btn-outline btn-sm hover:bg-[--color-orange]",
        onClick: () => setShowModal(true),
    };
    return (
        <div>
            <div className="">
                <HeaderCard
                    title={t('employeesPage.title')}
                    text={t('employeesPage.subtitle')}
                    ajout={canEdit ? t('employeesPage.addEmployee') : undefined}
                    path="/employees/new"
                    exportProps={canEdit ? {
                        model_name: "employee",
                        filename: "employes_export",
                        importUrl: "/import/employee",
                        ids: selectedIds,
                        onImportSuccess: () => fetchEmployees(pagination.page, searchTerm, filterBy)
                    } : undefined}
                    rightActions={
                        <button
                            type="button"
                            className={`btn btn-sm rounded-full ${showArchived ? 'btn-outline' : 'bg-[--color-primary] text-white'}`}
                            onClick={() => {
                                setSelectedIds([]);
                                setShowArchived(prev => !prev);
                            }}
                        >
                            {showArchived ? t('employeesPage.viewActive') : t('employeesPage.viewArchived')}
                        </button>
                    }
                    dropdownActions={!canEdit ? [] : [
                        {
                            label: t('employeesPage.contractsList'),
                            onClick: () => window.location.href = "/contracts",
                            icon: <RiFileList3Fill />
                        },
                        {
                            label: t('employeesPage.manageTags'),
                            onClick: () => setShowCategoryDrawer(true),
                            icon: <RiPriceTag3Fill />
                        },
                        {
                            label: t('employeesPage.manageDepartments'),
                            onClick: () => setShowDepartmentDrawer(true),
                            icon: <RiBuilding2Fill />
                        },
                        {
                            label: t('employeesPage.manageAssignments'),
                            onClick: () => setShowAssignmentDrawer(true),
                            icon: <RiFileList3Fill />
                        },
                    ]}
                />
            </div>

            <Modal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                title={t('employeesPage.assignTagTitle')}
                size={500}
                footer={
                    <>
                        <button
                            className="btn btn-sm btn-outline"
                            onClick={() => setShowModal(false)}
                        >
                            {t('employeesPage.cancel')}
                        </button>
                        <button
                            className="btn btn-sm btn-success"
                            disabled={!selectedCategory}
                            onClick={handleAffectCategory}
                        >
                            {t('employeesPage.assign')}
                        </button>
                    </>
                }
            >
                <CreatableSelect
                    isClearable
                    options={categoryOptions}
                    value={selectedCategory}
                    onChange={setSelectedCategory}
                    onCreateOption={handleCreateCategory}
                    placeholder={t('employeesPage.selectOrCreateTag')}
                />
            </Modal>

            <Modal
                isOpen={showOrganigramme}
                onClose={() => setShowOrganigramme(false)}
                title={t('organigrammeGroupe.titre')}
                size={500}
            >
                {showOrganigramme && (
                    <AffectationOrganigramme
                        employeIds={selectedIds}
                        departements={departmentOptions}
                        onAnnuler={() => setShowOrganigramme(false)}
                        onTermine={(modifies) => {
                            notify(t('organigrammeGroupe.termine', { n: modifies }), '', 2);
                            setShowOrganigramme(false);
                            setSelectedIds([]);
                            fetchEmployees(pagination.page, searchTerm, filterBy);
                            relireIndicateur();
                        }}
                    />
                )}
            </Modal>

            {/* New: Department modal */}
            <Modal
                isOpen={showDeptModal}
                onClose={() => setShowDeptModal(false)}
                title={t('employeesPage.assignDepartmentTitle')}
                size={500}
                footer={
                    <>
                        <button className="btn btn-sm btn-outline" onClick={() => setShowDeptModal(false)}>{t('employeesPage.cancel')}</button>
                        <button className="btn btn-sm btn-success" disabled={!selectedDepartment} onClick={handleAffectDepartment}>{t('employeesPage.assign')}</button>
                    </>
                }
            >
                <div className="form-control">
                    <label className="label">{t('employeesPage.departmentLabel')}</label>
                    <CreatableSelect
                        isClearable
                        options={departmentOptions}
                        value={selectedDepartment}
                        onChange={setSelectedDepartment}
                        onCreateOption={handleCreateDepartment}
                        placeholder={t('employeesPage.selectOrCreateDepartment')}
                    />
                </div>
            </Modal>

            {/* New: Assignment modal */}
            <Modal
                isOpen={showAssignmentModal}
                onClose={() => setShowAssignmentModal(false)}
                title={t('employeesPage.assignmentTitle')}
                size={500}
                footer={
                    <>
                        <button className="btn btn-sm btn-outline" onClick={() => setShowAssignmentModal(false)}>{t('employeesPage.cancel')}</button>
                        <button className="btn btn-sm btn-success" disabled={!selectedAssignment} onClick={handleAffectAssignment}>{t('employeesPage.assign')}</button>
                    </>
                }
            >
                <div className="form-control">
                    <label className="label">{t('employeesPage.assignmentLabel')}</label>
                    <CreatableSelect
                        isClearable
                        options={assignmentOptions}
                        value={selectedAssignment}
                        onChange={setSelectedAssignment}
                        onCreateOption={handleCreateAssignment}
                        placeholder={t('employeesPage.selectOrCreateAssignment')}
                    />
                </div>
            </Modal>

            <Modal
                isOpen={showArchiveConfirmModal}
                onClose={() => {
                    setShowArchiveConfirmModal(false);
                    setArchivePayslipWarning(null);
                }}
                title={archivePayslipWarning ? t('employeesPage.pendingPayslipsTitle') : t('employeesPage.confirmationTitle')}
                footer={
                    <>
                        <button
                            className="btn btn-sm btn-outline"
                            onClick={() => {
                                setShowArchiveConfirmModal(false);
                                setArchivePayslipWarning(null);
                            }}
                        >
                            {t('employeesPage.cancel')}
                        </button>
                        <button
                            className={`btn btn-sm ${isArchiving ? 'btn-error' : 'btn-success'}`}
                            onClick={confirmArchive}
                        >
                            {archivePayslipWarning ? t('employeesPage.deletePayslipsAndArchive') : t('employeesPage.confirm')}
                        </button>
                    </>
                }
            >
                {archivePayslipWarning ? (
                    <div className="space-y-3">
                        <p>
                            {t('employeesPage.pendingPayslipsWarning')}
                        </p>
                        <div className="max-h-56 overflow-y-auto rounded-lg border border-orange-200 bg-orange-50 p-3 text-sm text-orange-900">
                            {(archivePayslipWarning.payslips || []).map((payslip) => (
                                <div key={payslip.id} className="border-b border-orange-200 py-2 last:border-b-0">
                                    <p className="font-semibold">
                                        {payslip.employee_name} - {payslip.number || t('employeesPage.payslipFallback', { id: payslip.id })}
                                    </p>
                                    <p>{payslip.date_from} - {payslip.date_to} · {payslip.state}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <>
                        <p>{t('employeesPage.confirmArchiveQuestion', { action: isArchiving ? t('employeesPage.archiveAction') : t('employeesPage.unarchiveAction') })}</p>
                        <p className="text-sm text-yellow-600 mt-2">
                            {isArchiving
                                ? t('employeesPage.archiveContractsWarning')
                                : t('employeesPage.unarchiveContractsWarning')}
                        </p>
                    </>
                )}
            </Modal>

            {canEdit && aOffreRh() && !showArchived && (
                <IndicateurOrganigramme
                    key={versionIndicateur}
                    onFiltrer={appliquerFiltreOrganigramme}
                    onVoirDepartements={() => setShowDepartmentDrawer(true)}
                />
            )}

            <div className="">
                    <TableList
                        loading={loading}
                        columns={columns}
                        onSort={handleSort}
                        sortBy={sortBy}
                        selectable={canEdit}
                        model={"employee"}
                        data={data}
                        path="employees"
                        pagination={pagination}
                        searchQuery={searchTerm}
                        setSearchQuery={setSearchTerm}
                        filterBy={filterBy}
                        setFilterBy={setFilterBy}
                        toolbarActions={employeeToolbarFilters}
                        onPageChange={(page, search, filter) => handlePageChange(page, search, filter)}
                        onSelectedRowsChange={handleSelectedRowsChange} 
                        selectActions={() => (
                            <div className="flex gap-2">                                
                                {(! showArchived) ? (
                                <>
                                    <button
                                        className={etiquetteButtonProps.className}
                                        onClick={etiquetteButtonProps.onClick}
                                        disabled={etiquetteButtonProps.disabled || selectedIds.length === 0}
                                    >
                                        {etiquetteButtonProps.label}
                                    </button>
                                    {/* <button
                                        className="btn btn-sm btn-outline hover:bg-[--color-orange]"
                                        onClick={() => setShowDeptModal(true)}
                                        disabled={selectedIds.length === 0}
                                    >
                                        Affecter Département
                                    </button> */}
                                    {canEdit && aOffreRh() && (
                                        <button
                                            className="btn btn-sm btn-outline hover:bg-[--color-orange]"
                                            onClick={() => setShowOrganigramme(true)}
                                            disabled={selectedIds.length === 0}
                                        >
                                            {t('organigrammeGroupe.bouton')}
                                        </button>
                                    )}
                                    <button
                                        className="btn btn-sm btn-outline hover:bg-[--color-orange]"
                                        onClick={() => setShowAssignmentModal(true)}
                                        disabled={selectedIds.length === 0}
                                    >
                                        {t('employeesPage.assignmentTitle')}
                                    </button>
                                    <button
                                        className="btn btn-sm btn-outline"
                                        onClick={() => handleArchive(true)}
                                        disabled={selectedIds.length === 0}
                                    >
                                        <FaArchive className="mr-2" /> {t('employeesPage.archive')}
                                    </button>
                                </>
                                ):(
                                    <button
                                        className="btn btn-sm btn-outline"
                                        onClick={() => handleArchive(false)}
                                        disabled={selectedIds.length === 0}
                                    >
                                        <FaArchive className="mr-2" /> {t('employeesPage.unarchive')}
                                    </button>
                                )}
                            </div>
                        )}
                    />
            </div>
            <Drawer 
                isOpen={showCategoryDrawer}
                setIsOpen={open => {
                    if (!open) handleCloseCategoryDrawer();
                    else setShowCategoryDrawer(true);
                }}
                width={420}
                >
                <CategoryManager
                    categories={categories}
                    setCategories={setCategories}
                    onClose={handleCloseCategoryDrawer}
                />
            </Drawer>
            <Drawer 
                isOpen={showDepartmentDrawer}
                setIsOpen={open => {
                    if (!open) handleCloseDepartmentDrawer();
                    else setShowDepartmentDrawer(true);
                }}
                width={420}
            >
                <DepartmentManager
                    departments={departments}
                    setDepartments={setDepartments}
                    setDepartmentOptions={setDepartmentOptions}
                    onClose={handleCloseDepartmentDrawer}
                    showColumn={showDepartmentColumn}
                    setShowColumn={setShowDepartmentColumn}
                />
            </Drawer>
            <Drawer 
                isOpen={showAssignmentDrawer}
                setIsOpen={open => {
                    if (!open) handleCloseAssignmentDrawer();
                    else setShowAssignmentDrawer(true);
                }}
                width={420}
            >
                <AssignmentManager
                    assignments={assignments}
                    setAssignments={setAssignments}
                    setAssignmentOptions={setAssignmentOptions}
                    onClose={handleCloseAssignmentDrawer}
                    showColumn={showAssignmentColumn}
                    setShowColumn={setShowAssignmentColumn}
                />
            </Drawer>
        </div>
    );
}

export default Employees;
